/**
 * Visual PDF Annotator — drag & drop markers/labels/lines over the plan image.
 *
 * Uses the backend-rendered page PNGs (base64) as a canvas background, and
 * overlays an SVG layer for interactive annotations. Much lighter than react-pdf.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, Save, Download, Trash2, MousePointer2, Type, Square,
  Circle as CircleIcon, Camera, Radio, Minus, RectangleHorizontal,
  Wand2, Loader2, Undo2, ZoomIn, ZoomOut, Hash, Ruler,
  Target, Pentagon, Tag, MessageCircleQuestion, FileSpreadsheet,
  Eye, EyeOff, Send,
} from "lucide-react";
import api from "@/lib/api";

// Tool definitions
const TOOLS = [
  { id: "select", icon: MousePointer2, labelEs: "Seleccionar", labelEn: "Select" },
  { id: "label", icon: Type, labelEs: "Etiqueta", labelEn: "Label" },
  { id: "marker-circle", icon: CircleIcon, shape: "circle", labelEs: "Punto", labelEn: "Marker" },
  { id: "marker-camera", icon: Camera, shape: "camera", labelEs: "Cámara", labelEn: "Camera" },
  { id: "marker-ap", icon: Radio, shape: "ap", labelEs: "AP WiFi", labelEn: "WiFi AP" },
  { id: "marker-square", icon: Square, shape: "square", labelEs: "Equipo", labelEn: "Equipment" },
  { id: "line", icon: Minus, labelEs: "Ruta", labelEn: "Route" },
  { id: "rect", icon: RectangleHorizontal, labelEs: "Área", labelEn: "Area" },
  { id: "count", icon: Hash, labelEs: "Contar", labelEn: "Count" },
  { id: "measure", icon: Ruler, labelEs: "Medir", labelEn: "Measure" },
  { id: "autocount", icon: Target, labelEs: "Auto-contar", labelEn: "Auto-count" },
  { id: "polygon", icon: Pentagon, labelEs: "Área (sqft)", labelEn: "Area (sqft)" },
  { id: "zone", icon: Tag, labelEs: "Zona", labelEn: "Zone" },
];

// Price codes for the count tool (matches seeded price library)
const COUNT_CODES = [
  { code: "outlet", labelEs: "Tomacorriente", labelEn: "Outlet", category: "electrical" },
  { code: "switch", labelEs: "Interruptor", labelEn: "Switch", category: "electrical" },
  { code: "light_fixture", labelEs: "Luminaria", labelEn: "Light", category: "electrical" },
  { code: "junction_box", labelEs: "Caja", labelEn: "Junction Box", category: "electrical" },
  { code: "data_point", labelEs: "Punto Data", labelEn: "Data Point", category: "low_voltage" },
  { code: "access_point", labelEs: "AP WiFi", labelEn: "WiFi AP", category: "low_voltage" },
  { code: "cctv_camera", labelEs: "Cámara", labelEn: "Camera", category: "low_voltage" },
  { code: "rack", labelEs: "Rack", labelEn: "Rack", category: "low_voltage" },
];

const SYSTEMS = [
  { id: "low_voltage", color: "#EA580C", labelEs: "Bajo Voltaje", labelEn: "Low Voltage" },
  { id: "electrical", color: "#003366", labelEs: "Eléctrico", labelEn: "Electrical" },
  { id: "fiber", color: "#EAB308", labelEs: "Fibra", labelEn: "Fiber" },
  { id: "utp", color: "#2563EB", labelEs: "UTP", labelEn: "UTP" },
  { id: "conduit", color: "#DC2626", labelEs: "Conduit", labelEn: "Conduit" },
];

export default function Annotator() {
  const { estimationId } = useParams();
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  const navigate = useNavigate();

  const [pages, setPages] = useState([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [annotations, setAnnotations] = useState([]);
  const [tool, setTool] = useState("select");
  const [system, setSystem] = useState("low_voltage");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [lineDraft, setLineDraft] = useState(null); // {x1,y1}
  const [rectDraft, setRectDraft] = useState(null); // {x1,y1}
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoResult, setAutoResult] = useState(null);
  const [history, setHistory] = useState([]);

  // Count Tool state
  const [countCode, setCountCode] = useState("outlet");
  const [countClicks, setCountClicks] = useState([]); // ephemeral, per session [{x,y}]
  const [manualCounts, setManualCounts] = useState([]);

  // Measure Tool state
  const [scale, setScale] = useState(null); // {ft_per_px, ...} per current page
  const [measureDraft, setMeasureDraft] = useState(null); // {x1,y1}
  const [measurements, setMeasurements] = useState([]); // {page, x1,y1,x2,y2, ft}
  const [calibrationMode, setCalibrationMode] = useState(false);

  // TAKEOFF PRO state
  const [autoCountBox, setAutoCountBox] = useState(null); // drafting {x1,y1}
  const [autoCountLoading, setAutoCountLoading] = useState(false);
  const [polygonDraft, setPolygonDraft] = useState([]); // [[x,y],...]
  const [zones, setZones] = useState([]); // [{id,name,x1,y1,x2,y2,page}]
  const [zoneDraft, setZoneDraft] = useState(null);
  const [qaOpen, setQaOpen] = useState(false);
  const [qaHistory, setQaHistory] = useState([]); // [{q, a, ts}]
  const [qaQuestion, setQaQuestion] = useState("");
  const [qaLoading, setQaLoading] = useState(false);
  const [showQcOverlay, setShowQcOverlay] = useState(true);

  const svgRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/estimations/${estimationId}/pages`);
        setPages(data.pages || []);
        setAnnotations(data.saved_annotations || []);
        // Load scales and manual counts in parallel
        const [sc, mc] = await Promise.all([
          api.get(`/estimations/${estimationId}/scale`).catch(() => ({ data: { scales: {} } })),
          api.get(`/estimations/${estimationId}/manual-counts`).catch(() => ({ data: { counts: [] } })),
        ]);
        const sPage = sc.data.scales?.[String(0)] || null;
        setScale(sPage);
        setManualCounts(mc.data.counts || []);
      } catch (e) {
        console.error(e);
      } finally { setLoading(false); }
    })();
  }, [estimationId]);

  // Re-load scale when page changes
  useEffect(() => {
    api.get(`/estimations/${estimationId}/scale`).then(({ data }) => {
      setScale(data.scales?.[String(pageIdx)] || null);
    }).catch(() => {});
  }, [pageIdx, estimationId]);

  const currentPage = pages[pageIdx];
  const pageAnnos = useMemo(
    () => annotations.map((a, i) => ({ ...a, __i: i })).filter(a => (a.page ?? 0) === pageIdx),
    [annotations, pageIdx]
  );
  const systemColor = SYSTEMS.find(s => s.id === system)?.color || "#003366";

  const pushHistory = (prev) => setHistory(h => [...h.slice(-30), prev]);

  const undo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setAnnotations(prev);
  };

  const pointerToNormalized = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  };

  const onCanvasClick = (e) => {
    if (loading || !currentPage) return;
    if (tool === "select") return;
    const { x, y } = pointerToNormalized(e);

    if (tool === "label") {
      const text = prompt(es ? "Texto de la etiqueta:" : "Label text:", "");
      if (!text) return;
      pushHistory(annotations);
      setAnnotations(a => [...a, {
        type: "label", page: pageIdx, normalized: true,
        x, y, text, font_size: 10, system,
      }]);
      return;
    }
    if (tool.startsWith("marker-")) {
      const toolDef = TOOLS.find(t => t.id === tool);
      const text = prompt(es ? "Etiqueta (opcional):" : "Label (optional):", "");
      pushHistory(annotations);
      setAnnotations(a => [...a, {
        type: "marker", page: pageIdx, normalized: true,
        x, y, shape: toolDef.shape, size: 10, system,
        text: text || undefined, font_size: 9,
      }]);
      return;
    }
    if (tool === "line") {
      if (!lineDraft) { setLineDraft({ x, y }); return; }
      pushHistory(annotations);
      setAnnotations(a => [...a, {
        type: "line", page: pageIdx, normalized: true,
        points: [[lineDraft.x, lineDraft.y], [x, y]],
        system, width: 1.5, dashed: false,
      }]);
      setLineDraft(null);
      return;
    }
    if (tool === "rect") {
      if (!rectDraft) { setRectDraft({ x, y }); return; }
      const x1 = Math.min(rectDraft.x, x), y1 = Math.min(rectDraft.y, y);
      const x2 = Math.max(rectDraft.x, x), y2 = Math.max(rectDraft.y, y);
      pushHistory(annotations);
      setAnnotations(a => [...a, {
        type: "rect", page: pageIdx, normalized: true,
        x: x1, y: y1, x2, y2, system,
      }]);
      setRectDraft(null);
      return;
    }
    if (tool === "count") {
      // Stamp a numbered circle for this count code, accumulate clicks
      const next = [...countClicks, { x, y }];
      setCountClicks(next);
      pushHistory(annotations);
      setAnnotations(a => [...a, {
        type: "marker", page: pageIdx, normalized: true,
        x, y, shape: "circle", size: 8, system,
        text: `${COUNT_CODES.find(c => c.code === countCode)?.labelEn?.[0] || "#"}${next.length}`,
        font_size: 8, count_code: countCode, auto: false,
      }]);
      return;
    }
    if (tool === "measure") {
      if (calibrationMode) {
        if (!measureDraft) { setMeasureDraft({ x, y }); return; }
        // Compute pixel distance (in page pixels, not normalized)
        const W = currentPage.width, H = currentPage.height;
        const dx = (x - measureDraft.x) * W;
        const dy = (y - measureDraft.y) * H;
        const px = Math.sqrt(dx * dx + dy * dy);
        const realStr = prompt(
          es ? `Distancia real en pies (ft) para ${px.toFixed(1)} px:` : `Real distance in feet (ft) for ${px.toFixed(1)} px:`,
          "10"
        );
        const real = parseFloat(realStr);
        if (!real || real <= 0) { setMeasureDraft(null); return; }
        api.put(`/estimations/${estimationId}/scale`, {
          page: pageIdx, pixel_distance: px, real_distance: real, unit: "ft"
        }).then(({ data }) => {
          setScale(data.scales[String(pageIdx)]);
          setCalibrationMode(false);
          setMeasureDraft(null);
        });
        return;
      }
      // Measure mode: 2-click distance, render on canvas
      if (!measureDraft) { setMeasureDraft({ x, y }); return; }
      const W = currentPage.width, H = currentPage.height;
      const dx = (x - measureDraft.x) * W;
      const dy = (y - measureDraft.y) * H;
      const px = Math.sqrt(dx * dx + dy * dy);
      const ft = scale?.ft_per_px ? px * scale.ft_per_px : null;
      setMeasurements(m => [...m, {
        page: pageIdx, x1: measureDraft.x, y1: measureDraft.y, x2: x, y2: y, px, ft,
      }]);
      setMeasureDraft(null);
      return;
    }
    if (tool === "autocount") {
      // First click = top-left; second click = bottom-right → run template match
      if (!autoCountBox) { setAutoCountBox({ x1: x, y1: y }); return; }
      const x1 = Math.min(autoCountBox.x1, x), y1 = Math.min(autoCountBox.y1, y);
      const x2 = Math.max(autoCountBox.x1, x), y2 = Math.max(autoCountBox.y1, y);
      setAutoCountBox(null);
      const w = x2 - x1, h = y2 - y1;
      if (w < 0.01 || h < 0.01) { alert(es?"Caja muy pequeña, intenta de nuevo":"Box too small, try again"); return; }
      const multi = pages.length > 1
        ? window.confirm(es
            ? `Aplicar a TODAS las ${pages.length} páginas? (Cancelar = solo esta página)`
            : `Apply to ALL ${pages.length} pages? (Cancel = this page only)`)
        : false;
      setAutoCountLoading(true);
      (async () => {
        try {
          const applyTo = multi ? pages.map((_, i) => i) : [pageIdx];
          const { data } = await api.post(
            `/estimations/${estimationId}/autocount`,
            { page_index: pageIdx, bbox: { x: x1, y: y1, w, h },
              threshold: 0.82, apply_to_pages: applyTo }
          );
          const perPageMsg = multi
            ? `\n${es?"Por página":"Per page"}: ${Object.entries(data.per_page || {}).map(([p,n]) => `p${+p+1}:${n}`).join(" ")}`
            : "";
          const label = prompt(
            (es?`Se encontraron ${data.matches_count} símbolos.`:`Found ${data.matches_count} symbols.`)
              + perPageMsg + (es?"\nEtiqueta del símbolo:":"\nSymbol label:"),
            countCode || "symbol"
          );
          if (!label) return;
          pushHistory(annotations);
          const stamps = (data.matches || []).map((m, i) => ({
            type: "marker", page: m.page ?? pageIdx, normalized: true,
            x: m.x, y: m.y, shape: "circle", size: 8, system,
            text: `${label}${i + 1}`, font_size: 8,
            count_code: label, auto: true, source: "autocount",
            confidence: m.score,
          }));
          setAnnotations(a => [...a, ...stamps]);
          setCountClicks(c => [...c, ...stamps.map(s => ({ x: s.x, y: s.y }))]);
        } catch (err) {
          alert(err?.response?.data?.detail || "Auto-count failed");
        } finally { setAutoCountLoading(false); }
      })();
      return;
    }
    if (tool === "polygon") {
      // Click to add vertex; double-click detection via closing to first point
      const next = [...polygonDraft, [x, y]];
      // Close polygon if clicking near the first point and we have ≥3 points
      if (next.length >= 4) {
        const [fx, fy] = next[0];
        const dist = Math.sqrt((x - fx) ** 2 + (y - fy) ** 2);
        if (dist < 0.015) {
          // Close and compute area
          const pts = next.slice(0, -1);
          if (!scale?.ft_per_px) {
            alert(es?"Calibra primero la escala con la herramienta Medir":"Calibrate scale first with Measure tool");
            setPolygonDraft([]);
            return;
          }
          (async () => {
            try {
              const { data } = await api.post(
                `/estimations/${estimationId}/polygon-area`,
                { page_index: pageIdx, points: pts, calibration_ft_per_px: scale.ft_per_px }
              );
              const name = prompt(es?`Área: ${data.area_sqft} sqft. Nombre:`:`Area: ${data.area_sqft} sqft. Name:`, es?"Zona A":"Zone A") || "";
              pushHistory(annotations);
              setAnnotations(a => [...a, {
                type: "polygon", page: pageIdx, normalized: true,
                points: pts, system,
                text: `${name} · ${data.area_sqft} sqft`,
                area_sqft: data.area_sqft, perimeter_ft: data.perimeter_ft,
                auto: false,
              }]);
            } catch (err) {
              alert(err?.response?.data?.detail || "Area calc failed");
            } finally { setPolygonDraft([]); }
          })();
          return;
        }
      }
      setPolygonDraft(next);
      return;
    }
    if (tool === "zone") {
      if (!zoneDraft) { setZoneDraft({ x1: x, y1: y }); return; }
      const x1 = Math.min(zoneDraft.x1, x), y1 = Math.min(zoneDraft.y1, y);
      const x2 = Math.max(zoneDraft.x1, x), y2 = Math.max(zoneDraft.y1, y);
      setZoneDraft(null);
      const name = prompt(es?"Nombre de la zona:":"Zone name:", `Zone ${zones.length + 1}`);
      if (!name) return;
      const newZone = { id: `zone-${Date.now()}`, name, x1, y1, x2, y2, page: pageIdx };
      setZones(z => [...z, newZone]);
      pushHistory(annotations);
      setAnnotations(a => [...a, {
        type: "zone", page: pageIdx, normalized: true,
        x: x1, y: y1, x2, y2, system,
        text: name, zone_id: newZone.id, auto: false,
      }]);
      return;
    }
  };

  // Document Q&A over the plan
  const askQuestion = async () => {
    const q = qaQuestion.trim();
    if (!q) return;
    setQaLoading(true);
    try {
      const { data } = await api.post(`/estimations/${estimationId}/qa`, {
        question: q,
        language: es ? "es" : "en",
        page_indices: [pageIdx],
      });
      setQaHistory(h => [{ q, a: data.answer, ts: new Date() }, ...h]);
      setQaQuestion("");
    } catch (err) {
      setQaHistory(h => [{ q, a: `⚠️ ${err?.response?.data?.detail || "Error"}`, ts: new Date(), error: true }, ...h]);
    } finally { setQaLoading(false); }
  };

  // Takeoff export — collect markers/polygons/measures into unified rows
  const buildTakeoffRows = () => {
    const rows = [];
    // Helper: identify zone for a point
    const zoneFor = (pagePts) => {
      const z = zones.find(z =>
        z.page === pagePts.page &&
        pagePts.x >= z.x1 && pagePts.x <= z.x2 &&
        pagePts.y >= z.y1 && pagePts.y <= z.y2
      );
      return z?.name || "";
    };
    // Device counts grouped by label + zone
    const tally = {};
    annotations.forEach(a => {
      if (a.type === "marker" && a.status !== "pending") {
        const page = (a.page ?? 0) + 1;
        const zn = zoneFor({ page: a.page ?? 0, x: a.x, y: a.y });
        const symbol = a.count_code || a.shape || a.system;
        const key = `${page}|${zn}|${symbol}`;
        if (!tally[key]) tally[key] = {
          page, zone: zn, symbol, label: a.text || symbol,
          qty: 0, unit: "ea", length_ft: "", area_sqft: "", notes: "",
        };
        tally[key].qty += 1;
      }
    });
    Object.values(tally).forEach(r => rows.push(r));
    // Polygons as area rows
    annotations.forEach(a => {
      if (a.type === "polygon" && a.area_sqft) {
        rows.push({
          page: (a.page ?? 0) + 1, zone: a.text?.split(" · ")[0] || "",
          symbol: "area", label: a.text || "area",
          qty: 1, unit: "zone", length_ft: a.perimeter_ft,
          area_sqft: a.area_sqft, notes: "",
        });
      }
    });
    // Measurements as length rows
    measurements.forEach((m, i) => {
      rows.push({
        page: (m.page ?? 0) + 1, zone: "", symbol: "length",
        label: `Measurement #${i + 1}`,
        qty: 1, unit: "run", length_ft: m.ft ? m.ft.toFixed(2) : "",
        area_sqft: "", notes: "",
      });
    });
    return rows;
  };

  const downloadTakeoff = async (format = "xlsx") => {
    const rows = buildTakeoffRows();
    if (!rows.length) {
      alert(es ? "No hay datos para exportar. Cuenta dispositivos, crea zonas o mide áreas primero." : "Nothing to export yet.");
      return;
    }
    try {
      const { data } = await api.post(
        `/estimations/${estimationId}/takeoff.${format}`,
        { rows, language: es ? "es" : "en", project_name: "IQES" },
        { responseType: "blob" }
      );
      const blob = new Blob([data], {
        type: format === "csv"
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `takeoff-${estimationId.slice(0, 8)}.${format}`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.data?.detail || "Export failed");
    }
  };

  // Running totals for the live overlay
  const runningTotals = useMemo(() => {
    const totals = {};
    annotations.forEach(a => {
      if (a.type === "marker" && a.status !== "pending") {
        const key = a.count_code || a.shape || "other";
        totals[key] = (totals[key] || 0) + 1;
      }
    });
    let totalLengthFt = 0;
    measurements.forEach(m => { if (m.ft) totalLengthFt += m.ft; });
    let totalAreaSqft = 0;
    annotations.forEach(a => { if (a.type === "polygon" && a.area_sqft) totalAreaSqft += a.area_sqft; });
    return { devices: totals, lengthFt: totalLengthFt, areaSqft: totalAreaSqft };
  }, [annotations, measurements]);

  const deleteSelected = () => {
    if (selectedIdx == null) return;
    pushHistory(annotations);
    setAnnotations(a => a.filter((_, i) => i !== selectedIdx));
    setSelectedIdx(null);
  };

  const clearAll = () => {
    if (!window.confirm(es ? "¿Borrar TODAS las anotaciones?" : "Delete ALL annotations?")) return;
    pushHistory(annotations);
    setAnnotations([]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/estimations/${estimationId}/annotations`, { annotations });
    } finally { setSaving(false); }
  };

  const exportAnnotated = async () => {
    await save();
    const res = await api.post(
      `/estimations/${estimationId}/annotate`,
      { annotations },
      { responseType: "blob" }
    );
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `annotated-${estimationId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const commitCount = async () => {
    if (!countClicks.length) return;
    const positions = countClicks.map(c => [c.x, c.y]);
    try {
      const { data } = await api.post(
        `/estimations/${estimationId}/manual-counts`,
        { code: countCode, qty: countClicks.length, page: pageIdx, positions }
      );
      setManualCounts(m => [...m, data.entry]);
      setCountClicks([]);
      alert(es ? `✓ ${data.entry.qty} ${data.entry.label} añadidos al BOM` : `✓ ${data.entry.qty} ${data.entry.label} added to BOM`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    }
  };

  const deleteCount = async (id) => {
    if (!window.confirm(es ? "¿Borrar este conteo?" : "Delete this count?")) return;
    try {
      await api.delete(`/estimations/${estimationId}/manual-counts/${id}`);
      setManualCounts(m => m.filter(c => c.id !== id));
    } catch (e) { console.error(e); }
  };

  const autoAnnotate = async () => {
    setAutoLoading(true); setAutoResult(null);
    try {
      const { data } = await api.post(
        `/estimations/${estimationId}/auto-annotate-from-survey`
      );
      setAutoResult(data);
      // reload annotations
      const { data: pd } = await api.get(`/estimations/${estimationId}/pages`);
      setAnnotations(pd.saved_annotations || []);
    } catch (e) {
      setAutoResult({ error: e?.response?.data?.detail || "Error" });
    } finally { setAutoLoading(false); }
  };

  const aiAutoDraft = async () => {
    const userPrompt = prompt(
      es
        ? "Describe el proyecto (opcional) — ej: 'Oficina comercial 3000 sqft, priorizar CCTV y AP':"
        : "Describe the project (optional) — e.g., 'Commercial office 3000 sqft, prioritize CCTV and AP':",
      ""
    );
    if (userPrompt === null) return; // cancelled
    const subsCsv = prompt(
      es ? "Subsistemas (separados por coma), ej: cctv,data,wap,fire_alarm:" : "Subsystems (comma-separated), e.g., cctv,data,wap,fire_alarm:",
      "cctv,data,wap"
    );
    if (subsCsv === null) return;
    const maxPagesStr = prompt(
      es ? "¿Cuántas páginas analizar? (1-5, default 3)" : "How many pages to analyze? (1-5, default 3)",
      "3"
    );
    if (maxPagesStr === null) return;
    const subs = subsCsv.split(",").map(s => s.trim()).filter(Boolean);
    const maxPages = Math.max(1, Math.min(parseInt(maxPagesStr, 10) || 3, 5));
    setAutoLoading(true); setAutoResult(null);
    try {
      const { data } = await api.post(
        `/estimations/${estimationId}/ai-auto-draft`,
        { prompt: userPrompt || undefined, subsystems: subs, max_pages: maxPages }
      );
      // Stamp each proposal as PENDING so the user must approve/reject each one.
      const pending = (data.annotations || []).map(a => ({
        ...a, status: "pending",
      }));
      pushHistory(annotations);
      setAnnotations(a => [...a, ...pending]);
      setAutoResult({
        matched: data.proposals_count, total_devices: data.proposals_count,
        annotations_added: (data.proposals_count || 0) + (data.routes_count || 0),
        routes_count: data.routes_count || 0,
        unmatched: [],
        notes: `${data.pages_analyzed || 1} ${es?'páginas analizadas':'pages analyzed'} · ${data.proposals_count||0} ${es?'puntos':'devices'} · ${data.routes_count||0} ${es?'trazos':'routes'}. ${data.coverage_notes || ''}`,
      });
    } catch (e) {
      setAutoResult({ error: e?.response?.data?.detail || "Error" });
    } finally { setAutoLoading(false); }
  };

  // IA Trazos — approve/reject pending AI proposals
  const pendingAiCount = annotations.filter(a => a.status === "pending").length;
  const pendingMarkerCount = annotations.filter(a => a.status === "pending" && a.type === "marker").length;
  const pendingRouteCount = annotations.filter(a => a.status === "pending" && a.type === "line").length;

  const approveAllPending = () => {
    pushHistory(annotations);
    setAnnotations(a => a.map(x => x.status === "pending" ? { ...x, status: "approved" } : x));
  };
  const rejectAllPending = () => {
    pushHistory(annotations);
    setAnnotations(a => a.filter(x => x.status !== "pending"));
  };
  const approveOne = (idx) => {
    pushHistory(annotations);
    setAnnotations(a => a.map((x, i) => i === idx ? { ...x, status: "approved" } : x));
  };
  const rejectOne = (idx) => {
    pushHistory(annotations);
    setAnnotations(a => a.filter((_, i) => i !== idx));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F5F7FA]">
        <Loader2 className="animate-spin text-[#003366]" size={24} />
      </div>
    );
  }

  if (!pages.length) {
    return (
      <div className="p-6 max-w-xl mx-auto mt-10 iqes-card" data-testid="annotator-no-pages">
        <div className="text-sm text-slate-700">
          {es ? "Esta estimación no tiene páginas renderizadas." : "This estimation has no rendered pages."}
        </div>
        <button onClick={() => navigate(-1)} className="mt-4 text-xs text-[#003366] hover:underline">
          ← {es ? "Volver" : "Back"}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[#F5F7FA]" data-testid="annotator-page">
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-xl text-slate-600"
            data-testid="annotator-back-btn"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
              {es ? "Anotador Visual" : "Visual Annotator"}
            </div>
            <div className="text-sm font-medium text-slate-900">
              {es ? "Página" : "Page"} {pageIdx + 1} / {pages.length} · {annotations.length} {es ? "anotaciones" : "annotations"}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={autoAnnotate}
            disabled={autoLoading}
            data-testid="annotator-auto-btn"
            className="px-3 py-2 rounded-xl text-xs font-mono uppercase tracking-wider bg-gradient-to-r from-[#EA580C] to-[#C2410C] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
          >
            {autoLoading ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
            {es ? "Auto desde Survey" : "Auto from Survey"}
          </button>
          <button
            onClick={aiAutoDraft}
            disabled={autoLoading}
            data-testid="annotator-aidraft-btn"
            className="px-3 py-2 rounded-xl text-xs font-mono uppercase tracking-wider bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
          >
            {autoLoading ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
            {es ? "IA Diseño" : "AI Draft"}
          </button>
          <button
            onClick={undo}
            disabled={!history.length}
            className="p-2 hover:bg-slate-100 rounded-xl text-slate-600 disabled:opacity-30"
            data-testid="annotator-undo-btn"
            title={es ? "Deshacer" : "Undo"}
          >
            <Undo2 size={15} />
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="annotator-save-btn"
            className="px-3 py-2 rounded-xl text-xs font-mono uppercase tracking-wider border border-slate-300 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {es ? "Guardar" : "Save"}
          </button>
          <button
            onClick={exportAnnotated}
            data-testid="annotator-export-btn"
            className="px-3 py-2 rounded-xl text-xs font-mono uppercase tracking-wider bg-[#003366] text-white hover:bg-[#002244] flex items-center gap-1.5 shadow-sm"
          >
            <Download size={12} /> PDF
          </button>
        </div>
      </header>

      {/* Main layout */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Floating panel — IA Trazos pending proposals */}
        {pendingAiCount > 0 && (
          <div className="absolute top-3 right-3 z-20 bg-white border-2 border-[#A855F7] shadow-xl rounded-2xl p-3 w-72"
               data-testid="ai-pending-panel">
            <div className="flex items-center gap-2 mb-2">
              <Wand2 size={14} className="text-[#8B5CF6]" />
              <div className="text-xs font-mono uppercase tracking-wider text-[#8B5CF6] font-semibold">
                {es ? "IA propuestas" : "AI proposals"}
              </div>
              <span className="ml-auto text-xs font-mono bg-[#8B5CF6] text-white rounded-full px-2 py-0.5"
                    data-testid="ai-pending-count">{pendingAiCount}</span>
            </div>
            <div className="grid grid-cols-2 gap-1 mb-2 text-[10px] font-mono">
              <div className="bg-slate-50 rounded px-2 py-1 flex items-center justify-between">
                <span className="text-slate-500 uppercase">{es?"Puntos":"Devices"}</span>
                <span className="font-bold text-slate-800" data-testid="ai-pending-markers">{pendingMarkerCount}</span>
              </div>
              <div className="bg-slate-50 rounded px-2 py-1 flex items-center justify-between">
                <span className="text-slate-500 uppercase">{es?"Trazos":"Routes"}</span>
                <span className="font-bold text-slate-800" data-testid="ai-pending-routes">{pendingRouteCount}</span>
              </div>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              {es ? "Haz click sobre cada propuesta para aprobar/rechazar individualmente, o usa los botones masivos."
                  : "Click each proposal to approve or reject individually, or use the bulk actions."}
            </p>
            <div className="flex gap-2">
              <button onClick={approveAllPending}
                      data-testid="ai-approve-all-btn"
                      className="flex-1 text-xs font-mono uppercase tracking-wider bg-emerald-600 text-white rounded-lg px-2 py-1.5 hover:bg-emerald-700">
                ✓ {es ? "Aprobar todo" : "Approve all"}
              </button>
              <button onClick={rejectAllPending}
                      data-testid="ai-reject-all-btn"
                      className="flex-1 text-xs font-mono uppercase tracking-wider bg-red-600 text-white rounded-lg px-2 py-1.5 hover:bg-red-700">
                ✗ {es ? "Rechazar todo" : "Reject all"}
              </button>
            </div>
          </div>
        )}
        {/* Left toolbox */}
        <aside className="w-16 bg-white border-r border-slate-200 flex flex-col items-center py-3 gap-1.5" data-testid="annotator-toolbox">
          {TOOLS.map(tb => {
            const active = tool === tb.id;
            return (
              <button
                key={tb.id}
                onClick={() => { setTool(tb.id); setLineDraft(null); setRectDraft(null); }}
                data-testid={`tool-${tb.id}`}
                title={es ? tb.labelEs : tb.labelEn}
                className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
                  active
                    ? "bg-[#003366] text-white shadow-md"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <tb.icon size={17} strokeWidth={1.8} />
              </button>
            );
          })}
          <div className="w-8 h-px bg-slate-200 my-2" />
          <button
            onClick={deleteSelected}
            disabled={selectedIdx == null}
            className="w-11 h-11 rounded-xl flex items-center justify-center text-red-600 hover:bg-red-50 disabled:opacity-30"
            title={es ? "Borrar" : "Delete"}
            data-testid="tool-delete"
          >
            <Trash2 size={15} />
          </button>
          <button
            onClick={clearAll}
            className="w-11 h-11 rounded-xl flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 text-[10px] font-mono"
            title={es ? "Borrar todo" : "Clear all"}
            data-testid="tool-clear-all"
          >
            CLR
          </button>
        </aside>

        {/* Canvas */}
        <div className="flex-1 overflow-auto bg-slate-100 p-6 flex items-start justify-center" data-testid="annotator-canvas">
          <div className="relative inline-block shadow-lg bg-white" style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}>
            {currentPage?.image_b64 && (
              <img
                src={currentPage.image_b64}
                alt="plan"
                style={{ width: currentPage.width, height: currentPage.height, display: "block", maxWidth: "100%" }}
                draggable={false}
              />
            )}
            <svg
              ref={svgRef}
              className="absolute inset-0 w-full h-full"
              style={{ cursor: tool === "select" ? "default" : "crosshair" }}
              onClick={onCanvasClick}
              viewBox={`0 0 ${currentPage?.width || 1000} ${currentPage?.height || 1000}`}
              preserveAspectRatio="none"
            >
              {pageAnnos.map((a) => {
                const color = SYSTEMS.find(s => s.id === a.system)?.color || "#EAB308";
                const isSelected = selectedIdx === a.__i;
                const handleSelect = (e) => { e.stopPropagation(); if (tool === "select") setSelectedIdx(a.__i); };
                const W = currentPage.width, H = currentPage.height;
                const px = (a.x ?? 0) * W, py = (a.y ?? 0) * H;

                if (a.type === "marker") {
                  const size = (a.size || 10);
                  const isPending = a.status === "pending";
                  const opacity = isPending ? 0.55 : 0.9;
                  const strokeDash = isPending ? "4 3" : undefined;
                  return (
                    <g key={a.__i} onClick={handleSelect} data-testid={`ann-marker-${a.__i}`}
                       className={isSelected ? "opacity-100" : ""}
                       style={{ opacity: isSelected ? 1 : opacity }}>
                      {isPending && (
                        <circle cx={px} cy={py} r={size * 1.8} fill="#A855F7" fillOpacity="0.08"
                                stroke="#A855F7" strokeWidth="1" strokeDasharray="2 3">
                          <animate attributeName="r" values={`${size*1.4};${size*2.2};${size*1.4}`}
                                   dur="2s" repeatCount="indefinite" />
                        </circle>
                      )}
                      {a.shape === "camera" && (
                        <g>
                          <rect x={px - size} y={py - size*0.6} width={size*1.6} height={size*1.2} fill={color} fillOpacity="0.45" stroke={color} strokeWidth="1.5" strokeDasharray={strokeDash} />
                          <rect x={px + size*0.6} y={py - size*0.3} width={size*0.4} height={size*0.6} fill={color} fillOpacity="0.45" stroke={color} strokeWidth="1.5" strokeDasharray={strokeDash} />
                        </g>
                      )}
                      {a.shape === "ap" && (
                        <g>
                          <circle cx={px} cy={py} r={size*0.6} fill={color} fillOpacity="0.45" stroke={color} strokeWidth="1.5" strokeDasharray={strokeDash} />
                          <circle cx={px} cy={py} r={size*0.9} fill="none" stroke={color} strokeWidth="0.8" />
                          <circle cx={px} cy={py} r={size*1.2} fill="none" stroke={color} strokeWidth="0.8" />
                        </g>
                      )}
                      {a.shape === "circle" && (
                        <circle cx={px} cy={py} r={size} fill={color} fillOpacity="0.35" stroke={color} strokeWidth="1.5" strokeDasharray={strokeDash} />
                      )}
                      {a.shape === "square" && (
                        <rect x={px - size} y={py - size} width={size*2} height={size*2} fill={color} fillOpacity="0.35" stroke={color} strokeWidth="1.5" strokeDasharray={strokeDash} />
                      )}
                      {(a.shape === "mdf" || a.shape === "idf") && (
                        <g>
                          <rect x={px - size*1.4} y={py - size} width={size*2.8} height={size*2} fill={color} fillOpacity="0.3" stroke={color} strokeWidth="1.5" strokeDasharray={strokeDash} />
                          <text x={px - size*1.0} y={py + 3} fill={color} fontWeight="bold" fontSize="10">{a.shape.toUpperCase()}</text>
                        </g>
                      )}
                      {a.text && (
                        <g>
                          <rect x={px + size + 3} y={py - 10} width={a.text.length * 5.5 + 6} height={14} fill="white" fillOpacity="0.95" stroke={color} strokeWidth="1" />
                          <text x={px + size + 6} y={py + 1} fill={color} fontSize="10" fontWeight="500">{a.text}</text>
                        </g>
                      )}
                      {isPending && isSelected && (
                        <g>
                          <rect x={px - size - 2} y={py + size + 4} width={80} height={22} fill="white" stroke="#A855F7" strokeWidth="1" rx="4" />
                          <text x={px - size + 4} y={py + size + 18} fontSize="10" fontWeight="600" fill="#10B981"
                                style={{ cursor: "pointer" }} onClick={(e)=>{e.stopPropagation(); approveOne(a.__i);}}>✓ OK</text>
                          <text x={px - size + 38} y={py + size + 18} fontSize="10" fontWeight="600" fill="#DC2626"
                                style={{ cursor: "pointer" }} onClick={(e)=>{e.stopPropagation(); rejectOne(a.__i);}}>✗ NO</text>
                        </g>
                      )}
                      {isSelected && !isPending && (
                        <circle cx={px} cy={py} r={size + 6} fill="none" stroke="#3B82F6" strokeWidth="2" strokeDasharray="3 2" />
                      )}
                    </g>
                  );
                }
                if (a.type === "label") {
                  const t = a.text || "";
                  return (
                    <g key={a.__i} onClick={handleSelect} data-testid={`ann-label-${a.__i}`}>
                      <rect x={px - 4} y={py - 12} width={t.length * 5.5 + 8} height={16} fill="white" fillOpacity="0.95" stroke={color} strokeWidth="1" />
                      <text x={px} y={py} fill={color} fontSize="10" fontWeight="500">{t}</text>
                      {isSelected && <rect x={px - 6} y={py - 14} width={t.length * 5.5 + 12} height={20} fill="none" stroke="#3B82F6" strokeWidth="2" strokeDasharray="3 2" />}
                    </g>
                  );
                }
                if (a.type === "line") {
                  const pts = (a.points || []).map(p => `${p[0] * W},${p[1] * H}`).join(" ");
                  const isPendingLine = a.status === "pending";
                  const effectiveColor = isPendingLine ? "#A855F7" : color;
                  const effectiveStroke = a.dashed || isPendingLine ? "6 3" : undefined;
                  const mid = (a.points && a.points.length)
                    ? a.points[Math.floor(a.points.length / 2)]
                    : null;
                  return (
                    <g key={a.__i} data-testid={`ann-line-${a.__i}`}>
                      {isPendingLine && (
                        <polyline points={pts} fill="none" stroke="#A855F7"
                          strokeOpacity="0.15" strokeWidth={(a.width || 1.5) + 6} />
                      )}
                      <polyline points={pts} fill="none" stroke={effectiveColor}
                        strokeWidth={a.width || 1.5}
                        strokeDasharray={effectiveStroke}
                        strokeLinejoin="round" strokeLinecap="round"
                        onClick={handleSelect}
                        style={{ cursor: tool === "select" ? "pointer" : "crosshair" }} />
                      {isSelected && (
                        <polyline points={pts} fill="none" stroke="#3B82F6"
                          strokeWidth="2.5" strokeDasharray="3 2" strokeOpacity="0.8" />
                      )}
                      {isPendingLine && mid && (
                        <g>
                          <rect x={mid[0]*W - 40} y={mid[1]*H - 8} width={80} height={16}
                            fill="white" fillOpacity="0.95" stroke="#A855F7" strokeWidth="0.8" rx="3" />
                          <text x={mid[0]*W - 36} y={mid[1]*H + 3} fontSize="9" fontWeight="600"
                            fill="#10B981" style={{ cursor: "pointer" }}
                            onClick={(e)=>{e.stopPropagation(); approveOne(a.__i);}}>✓ OK</text>
                          <text x={mid[0]*W - 2} y={mid[1]*H + 3} fontSize="8" fill="#64748B">
                            {a.cable_type?.toUpperCase() || "CBL"}
                          </text>
                          <text x={mid[0]*W + 22} y={mid[1]*H + 3} fontSize="9" fontWeight="600"
                            fill="#DC2626" style={{ cursor: "pointer" }}
                            onClick={(e)=>{e.stopPropagation(); rejectOne(a.__i);}}>✗ NO</text>
                        </g>
                      )}
                      {!isPendingLine && a.text && mid && (
                        <g>
                          <rect x={mid[0]*W - a.text.length*2.8} y={mid[1]*H - 8}
                            width={a.text.length*5.5 + 4} height={14}
                            fill="white" fillOpacity="0.85" stroke={effectiveColor} strokeWidth="0.5" rx="2" />
                          <text x={mid[0]*W - a.text.length*2.8 + 3} y={mid[1]*H + 3}
                            fontSize="9" fill={effectiveColor}>{a.text}</text>
                        </g>
                      )}
                    </g>
                  );
                }
                if (a.type === "rect") {
                  const rx = (a.x || 0) * W, ry = (a.y || 0) * H;
                  const rw = Math.abs(((a.x2 || 0) - (a.x || 0)) * W);
                  const rh = Math.abs(((a.y2 || 0) - (a.y || 0)) * H);
                  return (
                    <g key={a.__i} onClick={handleSelect} data-testid={`ann-rect-${a.__i}`}>
                      <rect x={rx} y={ry} width={rw} height={rh}
                        fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1.5" />
                      {isSelected && <rect x={rx - 3} y={ry - 3} width={rw + 6} height={rh + 6}
                        fill="none" stroke="#3B82F6" strokeWidth="2" strokeDasharray="3 2" />}
                    </g>
                  );
                }
                if (a.type === "zone") {
                  const rx = (a.x || 0) * W, ry = (a.y || 0) * H;
                  const rw = Math.abs(((a.x2 || 0) - (a.x || 0)) * W);
                  const rh = Math.abs(((a.y2 || 0) - (a.y || 0)) * H);
                  return (
                    <g key={a.__i} onClick={handleSelect} data-testid={`ann-zone-${a.__i}`}>
                      <rect x={rx} y={ry} width={rw} height={rh}
                        fill="#7C3AED" fillOpacity="0.07" stroke="#7C3AED" strokeWidth="1.2"
                        strokeDasharray="8 4" rx="4" />
                      <rect x={rx + 4} y={ry + 4} width={Math.min(rw - 8, 120)} height={18}
                        fill="#7C3AED" fillOpacity="0.85" rx="3" />
                      <text x={rx + 10} y={ry + 17} fontSize="11" fontWeight="600" fill="white">
                        {a.text || "Zone"}
                      </text>
                      {isSelected && <rect x={rx - 3} y={ry - 3} width={rw + 6} height={rh + 6}
                        fill="none" stroke="#3B82F6" strokeWidth="2" strokeDasharray="3 2" />}
                    </g>
                  );
                }
                if (a.type === "polygon") {
                  const pts = (a.points || []).map(p => `${p[0] * W},${p[1] * H}`).join(" ");
                  const centroid = a.points?.length
                    ? [
                        a.points.reduce((s, p) => s + p[0], 0) / a.points.length,
                        a.points.reduce((s, p) => s + p[1], 0) / a.points.length,
                      ]
                    : [0.5, 0.5];
                  return (
                    <g key={a.__i} onClick={handleSelect} data-testid={`ann-polygon-${a.__i}`}>
                      <polygon points={pts}
                        fill="#0EA5E9" fillOpacity="0.12" stroke="#0EA5E9" strokeWidth="1.5" />
                      {a.text && (
                        <g>
                          <rect x={centroid[0]*W - 60} y={centroid[1]*H - 12} width={120} height={22}
                            fill="white" fillOpacity="0.95" stroke="#0EA5E9" strokeWidth="0.8" rx="4" />
                          <text x={centroid[0]*W} y={centroid[1]*H + 3}
                            textAnchor="middle" fontSize="10" fontWeight="600" fill="#0369A1">
                            {a.text}
                          </text>
                        </g>
                      )}
                      {isSelected && <polygon points={pts} fill="none"
                        stroke="#3B82F6" strokeWidth="2" strokeDasharray="3 2" />}
                    </g>
                  );
                }
                return null;
              })}
              {/* Autocount draft box */}
              {autoCountBox && tool === "autocount" && (
                <circle cx={autoCountBox.x1 * currentPage.width} cy={autoCountBox.y1 * currentPage.height}
                  r="6" fill="#F59E0B" stroke="white" strokeWidth="2" />
              )}
              {autoCountLoading && (
                <g>
                  <rect x={currentPage.width * 0.35} y={currentPage.height * 0.45}
                    width={currentPage.width * 0.3} height="40"
                    fill="white" stroke="#F59E0B" strokeWidth="2" rx="8" />
                  <text x={currentPage.width * 0.5} y={currentPage.height * 0.45 + 26}
                    textAnchor="middle" fontSize="12" fontWeight="600" fill="#B45309">
                    {es ? "Auto-contando símbolos…" : "Auto-counting symbols…"}
                  </text>
                </g>
              )}
              {/* Polygon draft preview */}
              {polygonDraft.length > 0 && tool === "polygon" && (
                <g>
                  <polyline points={polygonDraft.map(p => `${p[0]*currentPage.width},${p[1]*currentPage.height}`).join(" ")}
                    fill="none" stroke="#0EA5E9" strokeWidth="2" strokeDasharray="4 3" />
                  {polygonDraft.map((p, i) => (
                    <circle key={`pd-${i}`} cx={p[0]*currentPage.width} cy={p[1]*currentPage.height}
                      r="5" fill={i===0?"#10B981":"#0EA5E9"} stroke="white" strokeWidth="1.5" />
                  ))}
                </g>
              )}
              {zoneDraft && tool === "zone" && (
                <circle cx={zoneDraft.x1 * currentPage.width} cy={zoneDraft.y1 * currentPage.height}
                  r="6" fill="#7C3AED" stroke="white" strokeWidth="2" />
              )}
              {lineDraft && (
                <circle cx={lineDraft.x * currentPage.width} cy={lineDraft.y * currentPage.height}
                  r="5" fill="#3B82F6" stroke="white" strokeWidth="2" />
              )}
              {rectDraft && (
                <circle cx={rectDraft.x * currentPage.width} cy={rectDraft.y * currentPage.height}
                  r="5" fill="#3B82F6" stroke="white" strokeWidth="2" />
              )}
              {measureDraft && tool === "measure" && (
                <circle cx={measureDraft.x * currentPage.width} cy={measureDraft.y * currentPage.height}
                  r="6" fill={calibrationMode ? "#EA580C" : "#10B981"} stroke="white" strokeWidth="2" />
              )}
              {/* Render measurements for current page */}
              {measurements.filter(m => m.page === pageIdx).map((m, i) => {
                const W = currentPage.width, H = currentPage.height;
                const mx = (m.x1 + m.x2) / 2 * W;
                const my = (m.y1 + m.y2) / 2 * H;
                const label = m.ft != null ? `${m.ft.toFixed(1)} ft` : `${m.px.toFixed(0)} px`;
                return (
                  <g key={`meas-${i}`} data-testid={`measurement-${i}`}>
                    <line x1={m.x1 * W} y1={m.y1 * H} x2={m.x2 * W} y2={m.y2 * H}
                      stroke="#10B981" strokeWidth="2" strokeDasharray="6 3" />
                    <circle cx={m.x1 * W} cy={m.y1 * H} r="4" fill="#10B981" />
                    <circle cx={m.x2 * W} cy={m.y2 * H} r="4" fill="#10B981" />
                    <rect x={mx - label.length * 3.5} y={my - 10} width={label.length * 7 + 4}
                      height={16} fill="white" stroke="#10B981" strokeWidth="1" rx="4" />
                    <text x={mx} y={my + 1} fill="#047857" fontSize="11" fontWeight="600"
                      textAnchor="middle">{label}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {/* Right panel */}
        <aside className="w-72 bg-white border-l border-slate-200 flex flex-col">
          {/* Count Tool panel (visible when tool=count) */}
          {tool === "count" && (
            <div className="p-4 border-b border-slate-100 bg-amber-50/40" data-testid="count-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700">
                  {es ? "Herramienta Contar" : "Count Tool"}
                </div>
                <span className="text-xs font-mono font-semibold text-amber-900" data-testid="count-current">
                  {countClicks.length}
                </span>
              </div>
              <select
                value={countCode}
                onChange={e => setCountCode(e.target.value)}
                className="iqes-input mb-2 text-xs"
                data-testid="count-code-select"
              >
                {COUNT_CODES.map(c => (
                  <option key={c.code} value={c.code}>{es ? c.labelEs : c.labelEn}</option>
                ))}
              </select>
              <div className="flex gap-1.5">
                <button
                  onClick={commitCount}
                  disabled={!countClicks.length}
                  data-testid="count-commit-btn"
                  className="flex-1 bg-[#EA580C] text-white rounded-xl px-2 py-1.5 text-xs font-medium hover:bg-[#C2410C] disabled:opacity-40"
                >
                  + {es ? "Al BOM" : "To BOM"}
                </button>
                <button
                  onClick={() => setCountClicks([])}
                  disabled={!countClicks.length}
                  data-testid="count-reset-btn"
                  className="px-2 py-1.5 text-xs border border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-40"
                >
                  {es ? "Reset" : "Reset"}
                </button>
              </div>
              <p className="text-[10px] text-amber-800/80 mt-2 leading-relaxed">
                {es
                  ? "Haz click en los símbolos que YOLO no detectó. Luego confirma para agregar al BOM."
                  : "Click the symbols YOLO missed. Then commit to add them to BOM."}
              </p>
            </div>
          )}

          {/* Measure Tool panel (visible when tool=measure) */}
          {tool === "measure" && (
            <div className="p-4 border-b border-slate-100 bg-emerald-50/40" data-testid="measure-panel">
              <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 mb-2">
                {es ? "Herramienta Medir" : "Measure Tool"}
              </div>
              {!scale ? (
                <div className="text-xs bg-amber-100 text-amber-900 rounded-xl p-2 mb-2 leading-snug">
                  ⚠ {es ? "Sin calibración. Calibra primero." : "Not calibrated. Calibrate first."}
                </div>
              ) : (
                <div className="text-xs text-emerald-900 mb-2 font-mono">
                  {scale.ft_per_px.toFixed(4)} ft/px · {es ? "Página" : "Page"} {pageIdx + 1}
                </div>
              )}
              <button
                onClick={() => { setCalibrationMode(!calibrationMode); setMeasureDraft(null); }}
                data-testid="measure-calibrate-btn"
                className={`w-full rounded-xl px-2 py-1.5 text-xs font-medium mb-2 ${
                  calibrationMode
                    ? "bg-[#EA580C] text-white"
                    : "bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                }`}
              >
                {calibrationMode
                  ? (es ? "Cancelar calibración" : "Cancel calibration")
                  : (es ? "📏 Calibrar escala" : "📏 Calibrate scale")}
              </button>
              {calibrationMode && (
                <p className="text-[10px] text-amber-800 leading-relaxed mb-2">
                  {es ? "Haz click en 2 puntos con distancia conocida (ej: cualquier muro de 10 ft) e ingrésala." : "Click 2 points of known distance (e.g. any 10ft wall) and enter it."}
                </p>
              )}
              {measurements.filter(m => m.page === pageIdx).length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                    {es ? "Mediciones" : "Measurements"}
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1" data-testid="measurements-list">
                    {measurements.filter(m => m.page === pageIdx).map((m, i) => (
                      <div key={i} className="text-xs font-mono text-slate-700 px-2 py-1 bg-white rounded-lg border border-slate-200 flex justify-between">
                        <span>#{i + 1}</span>
                        <span className="font-semibold text-emerald-700">
                          {m.ft != null ? `${m.ft.toFixed(1)} ft` : `${m.px.toFixed(0)} px`}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setMeasurements([])}
                    className="w-full mt-1.5 text-[10px] text-slate-500 hover:text-red-600 font-mono"
                  >
                    {es ? "Borrar mediciones" : "Clear measurements"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Manual counts list panel */}
          {manualCounts.length > 0 && (
            <div className="p-4 border-b border-slate-100" data-testid="manual-counts-list">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                {es ? "Conteos manuales" : "Manual counts"} · {manualCounts.length}
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {manualCounts.map(c => (
                  <div key={c.id} className="flex items-center justify-between text-xs bg-amber-50/60 rounded-lg px-2 py-1.5"
                       data-testid={`manual-count-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 truncate">{c.label}</div>
                      <div className="text-[10px] font-mono text-slate-500">
                        ×{c.qty} · ${(c.material + c.labor).toFixed(0)}
                      </div>
                    </div>
                    <button onClick={() => deleteCount(c.id)}
                            className="p-1 text-slate-400 hover:text-red-600">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Takeoff Pro — running totals + export + Q&A */}
          <div className="p-4 border-b border-slate-100" data-testid="takeoff-pro-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                {es ? "Takeoff Pro · Totales en vivo" : "Takeoff Pro · Live totals"}
              </div>
              <button onClick={() => setShowQcOverlay(v => !v)} title={es?"QC overlay":"QC overlay"}
                data-testid="toggle-qc-overlay"
                className="text-slate-400 hover:text-[#003366]">
                {showQcOverlay ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
            </div>
            {/* Device tally */}
            <div className="bg-slate-50 rounded-xl p-2.5 mb-2 max-h-40 overflow-auto" data-testid="takeoff-running-totals">
              {Object.keys(runningTotals.devices).length === 0 ? (
                <div className="text-xs text-slate-400 italic text-center py-2">
                  {es ? "Cuenta o auto-cuenta para ver totales" : "Count or auto-count to see totals"}
                </div>
              ) : (
                Object.entries(runningTotals.devices).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-xs py-0.5">
                    <span className="text-slate-700 font-mono truncate">{k}</span>
                    <span className="font-bold text-[#003366]" data-testid={`tally-${k}`}>×{v}</span>
                  </div>
                ))
              )}
            </div>
            {(runningTotals.lengthFt > 0 || runningTotals.areaSqft > 0) && (
              <div className="grid grid-cols-2 gap-1.5 mb-2">
                {runningTotals.lengthFt > 0 && (
                  <div className="bg-emerald-50 rounded-lg px-2 py-1.5 text-[10px] font-mono" data-testid="tally-length">
                    <div className="text-emerald-700 uppercase tracking-wider">Long.</div>
                    <div className="text-emerald-900 font-bold text-sm">{runningTotals.lengthFt.toFixed(0)} ft</div>
                  </div>
                )}
                {runningTotals.areaSqft > 0 && (
                  <div className="bg-cyan-50 rounded-lg px-2 py-1.5 text-[10px] font-mono" data-testid="tally-area">
                    <div className="text-cyan-700 uppercase tracking-wider">Área</div>
                    <div className="text-cyan-900 font-bold text-sm">{runningTotals.areaSqft.toFixed(0)} sqft</div>
                  </div>
                )}
              </div>
            )}
            {/* Export buttons */}
            <div className="flex gap-1.5 mb-2">
              <button onClick={() => downloadTakeoff("xlsx")}
                data-testid="takeoff-xlsx-btn"
                className="flex-1 bg-white border border-[#003366] text-[#003366] rounded-xl px-2 py-1.5 text-[11px] font-medium hover:bg-[#003366]/5 inline-flex items-center justify-center gap-1">
                <FileSpreadsheet size={11} /> XLSX
              </button>
              <button onClick={() => downloadTakeoff("csv")}
                data-testid="takeoff-csv-btn"
                className="flex-1 bg-white border border-slate-300 text-slate-700 rounded-xl px-2 py-1.5 text-[11px] font-medium hover:bg-slate-50 inline-flex items-center justify-center gap-1">
                <Download size={11} /> CSV
              </button>
            </div>
            {/* Document Q&A */}
            <button onClick={() => setQaOpen(v => !v)}
              data-testid="takeoff-qa-btn"
              className={`w-full rounded-xl px-2 py-1.5 text-[11px] font-medium inline-flex items-center justify-center gap-1 ${
                qaOpen ? "bg-[#8B5CF6] text-white" : "bg-white border border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/5"
              }`}>
              <MessageCircleQuestion size={11} />
              {es ? "Preguntar a IA sobre el plano" : "Ask AI about this plan"}
            </button>
            {qaOpen && (
              <div className="mt-2 space-y-2" data-testid="qa-panel">
                <div className="flex gap-1">
                  <input
                    value={qaQuestion}
                    onChange={e => setQaQuestion(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && askQuestion()}
                    data-testid="qa-input"
                    placeholder={es?"¿Cuántas cámaras hay en el piso 2?":"How many cameras on floor 2?"}
                    className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded-lg"
                  />
                  <button onClick={askQuestion} disabled={qaLoading || !qaQuestion.trim()}
                    data-testid="qa-submit-btn"
                    className="bg-[#8B5CF6] text-white rounded-lg px-2 py-1 text-xs hover:bg-[#7C3AED] disabled:opacity-50">
                    {qaLoading ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                  </button>
                </div>
                <div className="max-h-64 overflow-auto space-y-2" data-testid="qa-history">
                  {qaHistory.map((h, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-2 border-l-2 border-[#8B5CF6]">
                      <div className="text-[10px] font-mono uppercase text-[#8B5CF6] mb-0.5">Q</div>
                      <div className="text-xs text-slate-700 mb-1">{h.q}</div>
                      <div className="text-[10px] font-mono uppercase text-slate-500 mb-0.5">A</div>
                      <div className={`text-xs ${h.error?"text-red-600":"text-slate-800"} whitespace-pre-wrap`}>{h.a}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-b border-slate-100">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
              {es ? "Sistema" : "System"}
            </div>
            <div className="space-y-1.5" data-testid="annotator-system-picker">
              {SYSTEMS.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSystem(s.id)}
                  data-testid={`system-${s.id}`}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-xs transition-colors ${
                    system === s.id ? "bg-slate-100 ring-1 ring-slate-300" : "hover:bg-slate-50"
                  }`}
                >
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="font-medium text-slate-700">{es ? s.labelEs : s.labelEn}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                {es ? "Zoom" : "Zoom"}
              </div>
              <div className="text-xs font-mono text-slate-600">{Math.round(zoom * 100)}%</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setZoom(z => Math.max(0.4, z - 0.1))}
                className="flex-1 p-1.5 border border-slate-200 rounded-xl hover:bg-slate-50"
                data-testid="zoom-out"><ZoomOut size={14} className="mx-auto" /></button>
              <button onClick={() => setZoom(1)}
                className="flex-1 p-1.5 border border-slate-200 rounded-xl hover:bg-slate-50 text-[10px] font-mono"
                data-testid="zoom-reset">1×</button>
              <button onClick={() => setZoom(z => Math.min(3, z + 0.1))}
                className="flex-1 p-1.5 border border-slate-200 rounded-xl hover:bg-slate-50"
                data-testid="zoom-in"><ZoomIn size={14} className="mx-auto" /></button>
            </div>
          </div>

          {pages.length > 1 && (
            <div className="p-4 border-b border-slate-100">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                {es ? "Páginas" : "Pages"}
              </div>
              <div className="grid grid-cols-4 gap-1.5" data-testid="annotator-pages">
                {pages.map((_, i) => (
                  <button key={i} onClick={() => setPageIdx(i)}
                    data-testid={`page-btn-${i}`}
                    className={`text-xs font-mono py-1.5 rounded-xl transition-colors ${
                      pageIdx === i ? "bg-[#003366] text-white" : "border border-slate-200 hover:bg-slate-50"
                    }`}>{i + 1}</button>
                ))}
              </div>
            </div>
          )}

          {autoResult && (
            <div className="p-4 border-b border-slate-100 text-xs" data-testid="annotator-auto-result">
              {autoResult.error ? (
                <div className="text-red-700 bg-red-50 rounded-xl p-2">{autoResult.error}</div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <div className="font-medium text-amber-900 mb-1">
                    {es ? "Auto-anotado" : "Auto-labeled"}: {autoResult.matched}/{autoResult.total_devices}
                  </div>
                  <div className="text-amber-800">
                    +{autoResult.annotations_added} {es ? "anotaciones" : "annotations"}
                  </div>
                  {autoResult.unmatched?.length > 0 && (
                    <div className="mt-2 text-[10px] text-amber-700">
                      {es ? "Sin match" : "Unmatched"}: {autoResult.unmatched.map(u => u.label).join(", ")}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-3" data-testid="annotator-list">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
              {es ? `Anotaciones en página ${pageIdx + 1}` : `Annotations on page ${pageIdx + 1}`}
            </div>
            {pageAnnos.length === 0 && (
              <div className="text-xs text-slate-400 italic py-4 text-center">
                {es ? "Ninguna. Elige una herramienta y haz click." : "None. Pick a tool and click."}
              </div>
            )}
            {pageAnnos.map(a => (
              <div key={a.__i}
                onClick={() => setSelectedIdx(a.__i)}
                data-testid={`anno-row-${a.__i}`}
                className={`text-xs px-2.5 py-1.5 rounded-xl cursor-pointer mb-1 transition-colors ${
                  selectedIdx === a.__i ? "bg-blue-50 ring-1 ring-blue-300" : "hover:bg-slate-50"
                }`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-slate-700">
                    {a.type} {a.auto && <span className="text-[9px] text-amber-600">·AUTO</span>}
                  </span>
                  <span className="w-2 h-2 rounded-full" style={{
                    backgroundColor: SYSTEMS.find(s => s.id === a.system)?.color || "#94A3B8"
                  }} />
                </div>
                {a.text && <div className="text-slate-600 truncate mt-0.5">{a.text}</div>}
              </div>
            ))}
          </div>
        </aside>
      </div>

      {/* Helper footer */}
      <div className="bg-white border-t border-slate-200 px-4 py-1.5 text-[11px] font-mono text-slate-500 flex items-center gap-4">
        <span>{es ? "Modo" : "Mode"}: <b className="text-slate-700">{tool}</b></span>
        <span>{es ? "Color" : "Color"}: <span className="inline-block w-2.5 h-2.5 rounded-full align-middle" style={{backgroundColor: systemColor}} /></span>
        {(tool === "line" || tool === "rect") && (lineDraft || rectDraft) && (
          <span className="text-blue-600">{es ? "Haz click en el punto final" : "Click end point"}</span>
        )}
      </div>
    </div>
  );
}
